import { z } from "zod";
import { lafayettePresentableUiPartNames, UI_PART_LIMITS, } from "./agent-ui-parts.js";
// How the model chooses a presentation part (CHT-715, ADR 0004). The tool registry is closed and has no presentation
// tool. So the model ends its answer with ONE delimited block of "directives". A directive holds a permitted part TYPE,
// the entity ids to show, and at most one short rationale. It holds no title, no snapshot, no text of a part.
//
// The SERVER does the rest (`packages/agent-runtime/src/stream/`):
//   1. It cuts the block out of the text stream, so the customer never sees it.
//   2. It checks every id against the ids that THIS run's tool results returned (grounding). An id the run never saw is
//      refused and counted.
//   3. It copies the display snapshot from the tool result, never from the model text.
//   4. It validates the built part with `agent-ui-parts.ts`. An invalid or ungrounded directive is dropped with a counted
//      reason (`UI_PART_DROP_REASONS`). It never crashes the stream.
export const PRESENTATION_BLOCK_OPEN = "<lafayette-parts>";
export const PRESENTATION_BLOCK_CLOSE = "</lafayette-parts>";
export const PRESENTATION_LIMITS = Object.freeze({
    /** The most UTF-8 bytes between the two tags. A larger block is dropped whole. */
    blockMaxBytes: 4_096,
    maxDirectives: UI_PART_LIMITS.maxPartsPerResponse,
    maxIdsPerDirective: UI_PART_LIMITS.collectionItems.max,
    /** The most characters of a rationale that the server reads. The part keeps `UI_PART_LIMITS.rationaleChars`. */
    rationaleInputChars: 600,
});
/** How many ids each type takes. `handoff-status` takes none: its subject is the conversation of the run. */
export const PRESENTATION_ID_COUNT = Object.freeze({
    recommendation: { min: 1, max: 1 },
    "recommendation-collection": { ...UI_PART_LIMITS.collectionItems },
    comparison: { ...UI_PART_LIMITS.comparisonItems },
    "action-status": { min: 1, max: 1 },
    "concierge-request": { min: 1, max: 1 },
    "handoff-status": { min: 0, max: 0 },
    "itinerary-preview": { min: 1, max: 1 },
    "payment-request-placeholder": { min: 1, max: 1 },
});
/** The tools whose results make an id grounded for a part type (design 5.7). The runtime builds its ledger from these. */
export const PRESENTATION_GROUNDING_TOOLS = Object.freeze({
    recommendation: ["searchCatalog", "getCatalogItem"],
    "recommendation-collection": ["searchCatalog", "getCatalogItem"],
    comparison: ["searchCatalog", "getCatalogItem"],
    "action-status": ["getRequestStatus"],
    "concierge-request": ["getRequestStatus"],
    "handoff-status": ["requestHandoff"],
    "itinerary-preview": ["getItineraryContext"],
    "payment-request-placeholder": ["getRequestStatus", "preparePayment"],
});
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
/**
 * One directive. Strict: a directive that carries any other key (a title, a url, a component) is refused whole, because the
 * model then tried to write content. `rationale` is read up to 600 characters and the server cuts it to 200 after it cleans it.
 */
export const presentationDirectiveSchema = z.strictObject({
    type: z.enum(lafayettePresentableUiPartNames),
    ids: z.array(z.string().regex(UUID_SHAPE)).max(PRESENTATION_LIMITS.maxIdsPerDirective),
    rationale: z.string().max(PRESENTATION_LIMITS.rationaleInputChars).nullish(),
});
/** The JSON between the two tags: an array of directives. */
export const presentationBlockSchema = z.array(z.unknown()).max(PRESENTATION_LIMITS.maxDirectives);
/**
 * Why a directive, a block or a part did not reach the customer as a part. The server counts each reason per run (the
 * `agent_run` log line); the customer sees safe text instead. Never a crash.
 */
export const uiPartDropReasons = Object.freeze([
    "BLOCK_UNTERMINATED",
    "BLOCK_TOO_LARGE",
    "BLOCK_MALFORMED",
    "BLOCK_TOO_MANY_DIRECTIVES",
    "TEXT_AFTER_BLOCK",
    "DIRECTIVE_INVALID",
    "ID_COUNT",
    "ID_UNGROUNDED",
    "NOT_GROUNDED",
    "DUPLICATE",
    "RATIONALE_REJECTED",
    "PART_INVALID",
    "PART_TOO_LARGE",
    "TOO_MANY_PARTS",
    "CLIENT_VERSION_UNSUPPORTED",
    "RUN_NOT_COMPLETED",
    // An ok result of a grounding tool did not match what the ledger reads: the shape of a tool output changed, or a tool is broken.
    "TOOL_RESULT_UNREADABLE",
]);
/** The text that the prompt gives the model about the block. The prompt owner includes it as is (the format is frozen). */
export const PRESENTATION_INSTRUCTION_V1 = [
    "To show cards in the app, end your answer with one block and write nothing after it:",
    `${PRESENTATION_BLOCK_OPEN}[{"type":"recommendation","ids":["<id>"],"rationale":"<one short sentence>"}]${PRESENTATION_BLOCK_CLOSE}`,
    `Allowed types: ${lafayettePresentableUiPartNames.join(", ")}.`,
    "Use only ids that your tools returned in this conversation turn. Never write a title, a link or any other field.",
    "The block is optional. Do not mention it in your answer text.",
].join("\n");
